import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

const CATEGORIES = ["All", "Hospitality", "Luxury Residential", "Commercial"];

export default function Work() {
  const [projects, setProjects] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("category");
  const [active, setActive] = useState(CATEGORIES.includes(initial) ? initial : "All");

  // Sync URL <-> state
  useEffect(() => {
    const urlCat = searchParams.get("category");
    const next = CATEGORIES.includes(urlCat) ? urlCat : "All";
    if (next !== active) setActive(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectCategory = (c) => {
    setActive(c);
    if (c === "All") setSearchParams({});
    else setSearchParams({ category: c });
  };

  useEffect(() => {
    const params = active === "All" ? {} : { category: active };
    api.get("/projects", { params })
      .then((r) => setProjects(r.data))
      .catch(() => {});
  }, [active]);

  return (
    <main className="bg-bone" data-testid="work-page">
      <section className="pt-40 md:pt-56 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-muteInk mb-6">— The Archive</p>
          <h1 className="font-serif text-[12vw] md:text-[8vw] leading-[0.9] tracking-tight text-ink">
            Work,<span className="italic text-clay"> selected.</span>
          </h1>
          <div className="mt-14 md:mt-20 flex flex-wrap items-center gap-2 md:gap-3 border-t border-borderWarm pt-8">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-5 py-2.5 text-[12px] uppercase tracking-[0.22em] border transition-colors duration-300 ${
                  active === c ? "bg-ink text-bone border-ink" : "border-borderWarm text-ink hover:border-ink"
                }`}
              >
                {c}
              </button>
            ))}
            <span className="ml-auto text-[11px] uppercase tracking-[0.28em] text-muteInk">
              {projects.length} projects
            </span>
          </div>
        </div>
      </section>

      <section className="pb-32 md:pb-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-8">
          {projects.map((p, i) => {
            const spans = i % 5 === 0 ? "md:col-span-8" : i % 5 === 1 ? "md:col-span-4" : i % 5 === 2 ? "md:col-span-5" : i % 5 === 3 ? "md:col-span-7" : "md:col-span-12";
            return (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className={`col-span-12 ${spans}`}
                data-testid={`work-card-${p.id}`}
              >
                <Link to={`/work/${p.id}`} className="block group">
                  <div className="clip-frame w-full h-[380px] md:h-[560px] hoverable relative">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-ink/25 transition-opacity duration-500" />
                  </div>
                  <div className="pt-5 flex items-start justify-between gap-6">
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl text-ink tracking-tight">{p.title}</h3>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.24em] text-muteInk">
                        {p.category} · {p.location} · {p.year}
                      </p>
                    </div>
                    <span className="text-clay text-xl group-hover:translate-x-1 transition-transform duration-300">↗</span>
                  </div>
                </Link>
              </motion.article>
            );
          })}
        </div>
      </section>
    </main>
  );
}
